import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ClassGoals from '../../components/ClassGoals';
import HouseRoom from '../../components/HouseRoom';
import Loading from '../../components/Loading';
import type { TileEdit } from '../../game/blocks';
import { Ocean as Engine, type OceanData } from '../../game/engine';
import { HOUSES } from '../../game/houses';
import { OceanLive } from '../../game/live';
import { worldFor } from '../../game/world';
import { REASONS, setAquariumNames } from '../../lib/aquarium';
import { shortDate, today } from '../../lib/dates';
import { isOpen } from '../../lib/kinds';
import {
  awardSandDollars, fetchOcean, fetchSandDollarAwards, fetchTiles, fetchWallets, resetTiles, undoSandDollars,
  type SandDollarAward,
  addGoal, deleteGoal, goalStatus, type Goal,
} from '../../lib/ocean';
import { useClassroom } from '../../state/Classroom';

// ─────────────────────────────────────────────────────────────────────────────
// The teacher's class ocean: the whole map (drag, zoom, full screen for the
// projector), giving sand dollars during class, and undoing
// students' mining and building if someone wrecks something.
// ─────────────────────────────────────────────────────────────────────────────

const AMOUNTS = [1, 2, 5, 10];
const START_BALANCE = 10;   // what a student has before their first award (ocean_wallets default)

export default function AquariumPage() {
  const { active, students, assignments, progressFor, hydrated, refresh, dueFor } = useClassroom();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const miniRef = useRef<HTMLCanvasElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const engine = useRef<Engine | null>(null);
  const dataRef = useRef<OceanData | null>(null);
  const tilesRef = useRef<TileEdit[]>([]);
  const [ready, setReady] = useState(0);
  const [data, setData] = useState<OceanData | null>(null);
  const [tiles, setTiles] = useState<TileEdit[]>([]);
  const [awards, setAwards] = useState<SandDollarAward[]>([]);
  const [wallets, setWallets] = useState<Map<string, number>>(new Map());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState(REASONS[0]);
  const [amount, setAmount] = useState(1);
  // What's typed in "Other" (kept as text, so typing 15 isn't swallowed by the 1 button).
  const [otherAmount, setOtherAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ text: string; undo?: string[] } | null>(null);
  const [full, setFull] = useState(false);
  const [roomId, setRoomId] = useState<string | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [goalWeek, setGoalWeek] = useState<string | null>(null);
  const loadGoals = useCallback(async () => {
    if (!active?.id) return;
    try { const r = await goalStatus(active.id); setGoals(r.goals); setGoalWeek(r.week_start); } catch { /* the migration isn't run yet */ }
  }, [active?.id]);
  useEffect(() => { void loadGoals(); const id = window.setInterval(() => void loadGoals(), 60000); return () => window.clearInterval(id); }, [loadGoals]);
  const [liveCount, setLiveCount] = useState(0);

  const cid = active?.id;
  const world = useMemo(() => worldFor(active?.ocean_seed), [active?.ocean_seed]);

  const reload = useCallback(async () => {
    if (!cid) return;
    try {
      const [d, a, w, t] = await Promise.all([fetchOcean(cid), fetchSandDollarAwards(cid, 60), fetchWallets(cid), fetchTiles(cid)]);
      dataRef.current = d;
      tilesRef.current = t;
      setData(d);
      setAwards(a);
      setWallets(new Map(w.map(x => [x.student_id, x.balance])));
      setTiles(t);
      engine.current?.setData(d);
      engine.current?.setEdits(t, true);
    } catch (e) { setNotice({ text: e instanceof Error ? e.message : 'Could not load the ocean.' }); }
  }, [cid]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const e = new Engine(canvasRef.current, 'spectate', world);
    engine.current = e;
    e.setMinimap(miniRef.current);
    if (dataRef.current) e.setData(dataRef.current);
    e.setEdits(tilesRef.current, true);
    e.events = { onHouseClick: id => setRoomId(id) };
    setReady(r => r + 1);
    return () => { e.destroy(); engine.current = null; };
  }, [hydrated, cid, world]);

  useEffect(() => { setSelected(new Set()); void reload(); }, [reload]);
  // Students build while the teacher watches: keep it fresh.
  useEffect(() => {
    const id = window.setInterval(() => { if (!document.hidden) void reload(); }, 8000);
    return () => window.clearInterval(id);
  }, [reload]);

  // Live: every student in the ocean right now, in every region (the teacher only listens).
  useEffect(() => {
    if (!engine.current || !cid) return;
    const l = new OceanLive(cid, null, world.w, {
      onPeer: m => engine.current?.peerUpdate(m),
      onLeave: id => engine.current?.peerLeft(id),
      onTile: t => engine.current?.setEdits([t], false),
      onBeasts: m => engine.current?.beastState(m),
      onEmote: (id, em) => engine.current?.emote(id, em),
    });
    const id = window.setInterval(() => setLiveCount(engine.current?.peerCount() ?? 0), 1000);
    return () => { window.clearInterval(id); l.leave(); };
  }, [ready, cid, world]);
  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), notice.undo ? 12000 : 5000);
    return () => window.clearTimeout(id);
  }, [notice]);
  useEffect(() => {
    const onFs = () => setFull(document.fullscreenElement === stage.current);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Quick picks from the work that's open now (sent out, not past due).
  const week = useMemo(() => assignments.filter(a => isOpen(a, today(), students.map(s => dueFor(a, s.user_id)))), [assignments, students, dueFor]);
  const finishedAll = useMemo(() => (week.length === 0 ? [] : students
    .filter(s => week.every(a => progressFor(a.id, s.user_id)?.completed_at)).map(s => s.user_id)), [week, students, progressFor]);
  const didSome = useMemo(() => students
    .filter(s => week.some(a => { const p = progressFor(a.id, s.user_id); return p && (p.completed_at || p.completions > 0 || p.seconds_spent > 0); }))
    .map(s => s.user_id), [week, students, progressFor]);

  const housesOf = useMemo(() => {
    const m = new Map<string, NonNullable<OceanData['houses']>>();
    for (const h of data?.houses ?? []) if (h.student_id) m.set(h.student_id, [...(m.get(h.student_id) ?? []), h]);
    return m;
  }, [data]);
  const builtBy = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of tiles) if (t.student_id) m.set(t.student_id, (m.get(t.student_id) ?? 0) + 1);
    return m;
  }, [tiles]);

  if (!hydrated) return <Loading />;
  if (!active) return <div className="notice">Create a class first.</div>;

  const nameOf = (id: string) => students.find(s => s.user_id === id)?.display_name ?? 'A student';
  const balanceOf = (id: string) => wallets.get(id) ?? START_BALANCE;
  const toggle = (id: string) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const pick = (ids: string[]) => setSelected(new Set(ids));

  const give = async () => {
    if (!selected.size || amount < 1) return;
    setBusy(true);
    try {
      const rows = await awardSandDollars(active.id, [...selected], amount, reason);
      setNotice({
        text: rows.length === 1 ? `${nameOf(rows[0].student_id)} got 🪙 ${amount}!` : `${rows.length} students got 🪙 ${amount} each!`,
        undo: rows.map(r => r.id),
      });
      setSelected(new Set());
      void reload();
    } catch (e) { setNotice({ text: e instanceof Error ? e.message : 'Could not give sand dollars.' }); }
    finally { setBusy(false); }
  };

  const undo = async (ids: string[]) => {
    try { await undoSandDollars(ids); setNotice({ text: 'Taken back.' }); void reload(); }
    catch (e) { setNotice({ text: e instanceof Error ? e.message : 'Could not take it back.' }); }
  };

  const reset = async (who: string | null, hours: number | null) => {
    const whose = who ? `${nameOf(who)}’s` : 'everyone’s';
    const when = hours == null ? 'all' : hours === 1 ? 'the last hour of' : 'the last day of';
    if (!window.confirm(`Undo ${when} ${whose} mining and building? The blocks go back to how the ocean started. Sand dollars spent aren’t refunded.`)) return;
    setBusy(true);
    try {
      const n = await resetTiles(active.id, who, hours == null ? null : new Date(Date.now() - hours * 3600e3));
      setNotice({ text: n ? `Reset ${n} block${n === 1 ? '' : 's'}.` : 'Nothing to undo.' });
      void reload();
    } catch (e) { setNotice({ text: e instanceof Error ? e.message : 'Could not undo that.' }); }
    finally { setBusy(false); }
  };

  const toggleFull = async () => {
    try { if (document.fullscreenElement) await document.exitFullscreen(); else await stage.current?.requestFullscreen(); }
    catch { /* not allowed: fine */ }
  };


  return (
    <div className="aq-page">
      <div className="row">
        <h1>Class ocean</h1>
        <span className="spacer" />
        <label className="check-line" style={{ marginTop: 0 }}>
          <input type="checkbox" checked={data?.names ?? true}
            onChange={async e => {
              const v = e.target.checked;
              setData(d => d && { ...d, names: v });
              await setAquariumNames(active.id, v).catch(() => {});
              void refresh(); void reload();
            }} />
          Students see each other's names
        </label>
      </div>
      <p className="sub">
        A shared sandbox: students catch fish with a net, mine and build blocks, craft, and buy fish houses to put anywhere in the ocean.
        Pickaxes and hammers (which break after 15 blocks), crafting and houses cost sand dollars, which come from you.
      </p>

      <div ref={stage} className={`ocean-admin-stage${full ? ' full' : ''}`}>
        <div className="ocean-admin-map">
          <canvas ref={canvasRef} className="ocean-canvas" aria-label="The class ocean map. Drag to look around; tap a fish house to look inside." />
          <canvas ref={miniRef} className="ocean-mini small-mini" aria-hidden />
          <div className="ocean-zoom">
            <button type="button" onClick={() => engine.current?.zoomBy(1.4)} aria-label="Zoom in">＋</button>
            <button type="button" onClick={() => engine.current?.zoomBy(1 / 1.4)} aria-label="Zoom out">－</button>
            <button type="button" onClick={() => engine.current?.fit()}>Fit</button>
            <button type="button" onClick={toggleFull}>{full ? 'Exit full screen' : '⛶ Full screen'}</button>
          </div>
          <div className="ocean-live-count" aria-live="polite">{liveCount > 0 ? `🟢 ${liveCount} in the ocean now` : '⚪ Nobody in the ocean right now'}</div>
        </div>

        <div className="panel ocean-give">
          <div className="panel-head"><h3>🪙 Give sand dollars</h3></div>
          <div className="aq-picks row">
            <span className="small muted">Select:</span>
            <button type="button" className="small" onClick={() => pick(students.map(s => s.user_id))}>Everyone ({students.length})</button>
            {week.length > 0 && (
              <>
                <button type="button" className="small" disabled={!finishedAll.length} onClick={() => pick(finishedAll)}>Finished all open work ({finishedAll.length})</button>
                <button type="button" className="small" disabled={!didSome.length} onClick={() => pick(didSome)}>Started some open work ({didSome.length})</button>
              </>
            )}
            {selected.size > 0 && <button type="button" className="small" onClick={() => pick([])}>Clear</button>}
          </div>
          {students.length === 0 ? <p className="small muted">No students yet.</p> : (
            <div className="student-chips">
              {students.map(s => (
                <button key={s.user_id} type="button" aria-pressed={selected.has(s.user_id)}
                  className={`student-chip${selected.has(s.user_id) ? ' on' : ''}`} onClick={() => toggle(s.user_id)}>
                  <strong>{s.display_name}</strong>
                  <span className="small">🪙 {balanceOf(s.user_id)}</span>
                </button>
              ))}
            </div>
          )}
          <div className="aq-bar-row" style={{ marginTop: 10 }}>
            <span className="small muted">How many</span>
            <div className="chips">
              {AMOUNTS.map(n => <button key={n} type="button" aria-pressed={!otherAmount && amount === n} className={`chip${!otherAmount && amount === n ? ' on' : ''}`} onClick={() => { setAmount(n); setOtherAmount(''); }}>🪙 {n}</button>)}
              <input className={`chip-input amount${otherAmount ? ' on' : ''}`} type="text" inputMode="numeric" maxLength={4} aria-label="Another amount"
                value={otherAmount} placeholder="Other"
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                  const typed = Number(digits) > 1000 ? '1000' : digits;
                  setOtherAmount(typed);
                  const n = Math.min(1000, Number(typed));
                  setAmount(n >= 1 ? n : 1);
                }} />
            </div>
          </div>
          <div className="aq-bar-row" style={{ marginTop: 10 }}>
            <span className="small muted">For</span>
            <div className="chips">
              {REASONS.map(r => <button key={r} type="button" aria-pressed={reason === r} className={`chip${reason === r ? ' on' : ''}`} onClick={() => setReason(r)}>{r}</button>)}
              <input className={`chip-input${REASONS.includes(reason) ? '' : ' on'}`} value={REASONS.includes(reason) ? '' : reason} maxLength={120} placeholder="Other reason…" onChange={e => setReason(e.target.value || REASONS[0])} />
            </div>
          </div>
          <div className="aq-bar-row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="primary big" disabled={busy || !selected.size} onClick={give}>
              {busy ? 'Giving…' : !selected.size ? 'Select students' : `Give 🪙 ${amount}${selected.size > 1 ? ` to ${selected.size} students` : ''}`}
            </button>
          </div>
        </div>

        {roomId && data?.houses.some(h => h.id === roomId) && (
          <HouseRoom house={data.houses.find(h => h.id === roomId)!} editable={false} onClose={() => setRoomId(null)} />
        )}

        {notice && (
          <div className="aq-toast" role="status">
            <span>{notice.text}</span>
            {notice.undo && <button type="button" className="small" onClick={() => { const ids = notice.undo!; setNotice(null); void undo(ids); }}>Undo</button>}
            <button type="button" className="icon" aria-label="Dismiss" onClick={() => setNotice(null)}>✕</button>
          </div>
        )}
      </div>

      <div className="panel" style={{ marginTop: 20 }}>
        <div className="panel-head"><h3>🎯 Class goals this week</h3><span className="small muted">The whole class works on them together in the ocean; when one is reached, every student gets its sand dollars.</span></div>
        <ClassGoals goals={goals}
          onAdd={async (kind, target, reward) => {
            if (!goalWeek) return;
            try { await addGoal(active.id, goalWeek, kind, target, reward); await loadGoals(); }
            catch (e) { setNotice({ text: e instanceof Error ? e.message : 'Could not add the goal.' }); }
          }}
          onDelete={async id => { try { await deleteGoal(id); await loadGoals(); } catch { /* fine */ } }} />
      </div>

      <div className="notice small" style={{ marginTop: 20 }}>🕹️ Games students make, and games waiting for your approval, are on the <Link to="/shop">Shop</Link> page.</div>

      <div className="panel" style={{ marginTop: 20 }}>
        <div className="panel-head"><h3>Students</h3><span className="small muted">{data?.houses.length ?? 0} fish house{data?.houses.length === 1 ? '' : 's'} in the ocean</span></div>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Student</th><th>Sand dollars</th><th>Houses · fish</th><th>Blocks changed</th><th /></tr></thead>
            <tbody>
              {students.map(s => {
                const houses = housesOf.get(s.user_id) ?? [];
                const home = houses[houses.length - 1];
                const built = builtBy.get(s.user_id) ?? 0;
                return (
                  <tr key={s.user_id}>
                    <td>{s.display_name}</td>
                    <td>🪙 {balanceOf(s.user_id)}</td>
                    <td className="small">{houses.length ? houses.map(h => HOUSES[h.kind].name).join(', ') : '—'}{houses.length ? ` · ${houses.reduce((a, h) => a + h.fish.length, 0)} fish` : ''}</td>
                    <td>
                      {built}
                      {built > 0 && (
                        <span className="row" style={{ gap: 4, marginTop: 4 }}>
                          <button type="button" className="small" disabled={busy} onClick={() => void reset(s.user_id, 1)}>Undo last hour</button>
                          <button type="button" className="small" disabled={busy} onClick={() => void reset(s.user_id, 24)}>Undo today</button>
                        </span>
                      )}
                    </td>
                    <td>{home && <button type="button" className="small" title="Show their newest fish house" onClick={() => { engine.current?.focusAt(home.x, home.y - 60); stage.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>Show</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="row" style={{ marginTop: 12 }}>
          <span className="small muted">{tiles.length} block{tiles.length === 1 ? '' : 's'} changed in this ocean.</span>
          <span className="spacer" />
          <button type="button" className="small danger" disabled={busy || tiles.length === 0} onClick={() => void reset(null, null)}>Reset all mining & building</button>
        </div>
      </div>

      {awards.length > 0 && (
        <div className="panel" style={{ marginTop: 20 }}>
          <div className="panel-head"><h3>Recent sand dollars</h3></div>
          <div className="aq-recent">
            {awards.slice(0, 25).map(r => (
              <div key={r.id} className="aq-recent-row">
                <span className="sd-amount">🪙 {r.amount}</span>
                <span className="grow"><strong>{nameOf(r.student_id)}</strong>{r.reason && <span className="small muted"> · {r.reason}</span>}</span>
                <span className="small muted">{shortDate(r.created_at)}</span>
                <button type="button" className="small" onClick={() => void undo([r.id])}>Take back</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
